//
//  PinnedQaProgramChecklist.tsx
//
//  Copyright:
//    NewtonGreen Technologies Pty. Ltd.
//    Level 4, 175 Scott Street
//    Newcastle, NSW, 2300
//    Australia
//
//    E-mail: support@newtongreen.com
//    Tel: (02) 4925 5288
//
//    All Rights Reserved.
//
//  Function of this module:
//    This module renders the Pinned QA Program Checklist component.
//

import {
    ActionIcon,
    Badge,
    Box,
    Button,
    Group,
    Loader,
    Progress,
    Stack,
    Text,
    Tooltip,
    UnstyledButton
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { useEffect, useRef, useState, type FC } from "react";
import { Link } from "react-router-dom";

import { Permission, type TrialQaProgramChecklistView } from "src/api/Tmd.dtos";
import AppSettings from "src/AppSettings";
import Icon from "src/components/common/Icon";
import useCurrentUserPermissions from "src/hooks/authentication/useCurrentUserPermissions";
import useHasPermission from "src/hooks/authentication/useHasPermission";
import useTrialQaProgramChecklist from "src/hooks/data/useTrialQaProgramChecklist";
import usePinnedQaProgramChecklist from "src/hooks/preferences/usePinnedQaProgramChecklist";

import classes from "./PinnedQaProgramChecklist.module.css";
import QaProgramChecklistView from "./QaProgramChecklistView";

/** Widths the shell reserves for the pinned checklist region, by collapsed state. */
export const collapsedPinnedChecklistWidth = 52;
export const expandedPinnedChecklistWidth = 420;

/** Defines the optional presentation controls for the pinned checklist. */
interface PinnedQaProgramChecklistProps {
    collapsed?: boolean;
    onClose?: () => void;
}

/** Counts the applicable tasks still outstanding on the supplied checklist. */
const outstandingCount = (checklist?: TrialQaProgramChecklistView) =>
    Math.max((checklist?.applicableCount ?? 0) - (checklist?.completedCount ?? 0), 0);

/**
 * Reports progress against the applicable tasks in the width the panel has.
 *
 * Postconditions: Tasks marked not applicable are excluded from both figures, matching the page.
 */
const PinnedQaProgramChecklistProgress: FC<{ checklist: TrialQaProgramChecklistView }> = ({
    checklist
}) => (
    <Stack
        gap={6}
        px="sm"
        pt="sm"
    >
        <Text size="sm">
            <Text
                span
                fw={600}
            >
                {checklist.completedCount ?? 0} of {checklist.applicableCount ?? 0} complete
            </Text>
            {" · "}
            {outstandingCount(checklist)} outstanding
        </Text>
        <Progress
            value={checklist.progressPercent ?? 0}
            color="primary"
            size="sm"
            radius="xl"
        />
    </Stack>
);

/** Renders the checklist pinned to the current user's modern application shell. */
const PinnedQaProgramChecklist: FC<PinnedQaProgramChecklistProps> = ({
    collapsed = false,
    onClose
}) => {
    const { pin, clearPin, toggleCollapsed } = usePinnedQaProgramChecklist();
    const checklist = useTrialQaProgramChecklist(pin?.trialId);
    const canUpdate = useHasPermission(
        useCurrentUserPermissions(),
        Permission.TrialQaProgramChecklistUpdate,
        pin?.trialId
    );
    const [showCompleted, setShowCompleted] = useState(false);
    const cleared = useRef(false);

    useEffect(() => {
        if (pin && !checklist.loading && (checklist.error || !checklist.data) && !cleared.current) {
            cleared.current = true;
            clearPin();
            notifications.show({
                color: "warning",
                message: "The pinned QA program checklist is no longer available and was unpinned."
            });
        }
    }, [checklist.data, checklist.error, checklist.loading, clearPin, pin]);

    if (!pin) {
        return null;
    }
    if (collapsed) {
        return (
            <Stack
                align="center"
                gap="xs"
                py="sm"
                h="100%"
            >
                <Tooltip
                    label="Expand pinned QA program checklist"
                    position="left"
                >
                    <UnstyledButton
                        onClick={toggleCollapsed}
                        aria-label="Expand pinned QA program checklist"
                    >
                        <Stack
                            align="center"
                            gap="xs"
                        >
                            <Icon
                                icon={AppSettings.icons.pin}
                                color="var(--mantine-color-primary-6)"
                            />
                            <Badge
                                color="secondary.6"
                                autoContrast
                                size="sm"
                                circle
                            >
                                {outstandingCount(checklist.data)}
                            </Badge>
                            <Text
                                size="xs"
                                c="dimmed"
                                tt="uppercase"
                                className={classes.verticalLabel}
                            >
                                QA Checklist
                            </Text>
                        </Stack>
                    </UnstyledButton>
                </Tooltip>
                <Tooltip
                    label="Unpin checklist"
                    position="left"
                >
                    <ActionIcon
                        variant="subtle"
                        color="error"
                        mt="auto"
                        onClick={clearPin}
                        aria-label="Unpin checklist"
                    >
                        <Icon icon={AppSettings.icons.delete} />
                    </ActionIcon>
                </Tooltip>
            </Stack>
        );
    }

    return (
        <Stack
            h="100%"
            gap={0}
            style={{ overflow: "hidden" }}
        >
            <Group
                bg="primary"
                c="white"
                p="sm"
                justify="space-between"
                wrap="nowrap"
            >
                <Box style={{ minWidth: 0 }}>
                    <Text
                        size="xs"
                        lineClamp={1}
                        opacity={0.8}
                    >
                        {pin.trialName}
                    </Text>
                    <Text
                        fw={700}
                        lineClamp={1}
                    >
                        QA Program Checklist
                    </Text>
                </Box>
                <Group
                    gap={4}
                    wrap="nowrap"
                >
                    {!onClose && (
                        <ActionIcon
                            variant="subtle"
                            color="gray.0"
                            onClick={toggleCollapsed}
                            aria-label="Collapse pinned checklist"
                        >
                            <Icon icon={AppSettings.icons.arrowRight} />
                        </ActionIcon>
                    )}
                    <ActionIcon
                        variant="subtle"
                        color="gray.0"
                        onClick={() => {
                            clearPin();
                            onClose?.();
                        }}
                        aria-label="Unpin checklist"
                    >
                        <Icon icon={AppSettings.icons.delete} />
                    </ActionIcon>
                </Group>
            </Group>

            {checklist.data && <PinnedQaProgramChecklistProgress checklist={checklist.data} />}

            <Box
                p="sm"
                style={{ flex: 1, overflow: "auto" }}
            >
                {checklist.loading && <Loader size="sm" />}
                {checklist.data && (
                    <QaProgramChecklistView
                        checklist={checklist.data}
                        canUpdate={canUpdate}
                        showCompleted={showCompleted}
                        onCompletionChange={checklist.setCompletionState}
                        onAddNote={checklist.addNote}
                    />
                )}
            </Box>

            <Group
                px="xs"
                py={4}
                justify="space-between"
                wrap="nowrap"
                style={{ borderTop: "1px solid var(--mantine-color-default-border)" }}
            >
                <Button
                    variant="subtle"
                    size="compact-xs"
                    onClick={() => setShowCompleted(current => !current)}
                >
                    {showCompleted ? "Hide completed" : "Show completed"}
                </Button>
                <Button
                    variant="subtle"
                    size="compact-xs"
                    component={Link}
                    to={`/trials/${pin.trialId}/qa-program-checklist`}
                    onClick={onClose}
                >
                    Open full checklist
                </Button>
            </Group>
        </Stack>
    );
};

export default PinnedQaProgramChecklist;
